import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Icon } from '@popety-io/ui'

const PanelOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 998;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;
`

const PanelContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  right: 0;
  top: 64px; /* Below the header */
  height: calc(100vh - 64px);
  width: 500px;
  background: #ffffff;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 999;
  transform: ${({ $isOpen }) =>
    $isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 768px) {
    width: 100%;
    top: 64px;
    height: calc(100vh - 64px);
  }
`

const PanelHeader = styled.div`
  background: #ffffff;
  border-bottom: 1px solid #e5e5e5;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
`

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  color: #6b6b6b;
  padding: 0;

  &:hover {
    background: #f5f5f5;
    color: #1a1a1a;
  }
`

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`

export interface ActionPanelProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  showOverlay?: boolean
  className?: string
}

export const ActionPanel: React.FC<ActionPanelProps> = ({
  isOpen,
  onClose,
  title,
  children,
  showOverlay = true,
  className,
}) => {
  const { t } = useTranslation()

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  return (
    <>
      {showOverlay && <PanelOverlay $isOpen={isOpen} onClick={onClose} />}
      <PanelContainer $isOpen={isOpen} className={className}>
        <PanelHeader>
          <PanelTitle>{title}</PanelTitle>
          <CloseButton onClick={onClose} aria-label={t('landAnalysis.tagsPanel.closePanel')}>
            <Icon name="x" size="small" />
          </CloseButton>
        </PanelHeader>
        <PanelContent>{children}</PanelContent>
      </PanelContainer>
    </>
  )
}
